import { useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import toast from "react-hot-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AgentActionsMenu } from "@/components/Agents/AgentActionsMenu";
import { AgentEmailSection } from "@/components/Agents/AgentEmailSection";
import { AgentScheduleSection } from "@/components/Agents/AgentScheduleSection";
import { ArchiveAgentDialog } from "@/components/Agents/ArchiveAgentDialog";
import { useAgents } from "@/components/Agents/AgentsContext";
import { useSession } from "@/auth/SessionProvider";
import {
  AGENT_NAV_ITEMS,
  agentSectionPath,
  agentSettingsNavItems,
  isAgentSettingsSectionId,
  parseAgentWorkspacePath,
  type AgentEmailSettingsPane,
  type AgentSectionId,
} from "@/lib/agentNav";
import { agentsHref } from "@/lib/atwAgentsHost";
import { agentDirectory, timelineForAgent, type TimelineEvent } from "@/lib/agentsDemoData";

function kindLabel(kind: TimelineEvent["kind"]): string {
  return kind.replace(/_/g, " ");
}

function resultBadge(result?: TimelineEvent["result"]) {
  if (!result) return null;
  if (result === "success") return <Badge>Success</Badge>;
  if (result === "fail") return <Badge variant="destructive">Fail</Badge>;
  if (result === "stopped") return <Badge variant="secondary">Stopped</Badge>;
  if (result === "partial") return <Badge variant="outline">Partial</Badge>;
  return <Badge variant="outline">Pending</Badge>;
}

export function AgentDetailPage() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const agentId = typeof params?.agentId === "string" ? params.agentId : "";
  const settingParam = typeof params?.setting === "string" ? params.setting : undefined;
  const parsed = parseAgentWorkspacePath(location.pathname);
  const section: AgentSectionId = parsed?.section ?? "dashboard";
  const emailPane: AgentEmailSettingsPane | undefined = parsed?.emailPane;
  const {
    getAgent,
    archiveAgent,
    setAgentStatus,
    duplicateAgent,
    may,
    viewOnly,
    org,
  } = useAgents();
  const session = useSession();
  const agent = getAgent(agentId);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const people = useMemo(
    () => agentDirectory(org.users, session.data?.user),
    [org.users, session.data?.user],
  );

  const events = useMemo(() => (agent ? timelineForAgent(agent.id) : []), [agent]);
  const sharedNames = useMemo(() => {
    if (!agent) return [];
    return (agent.sharedWithUserIds ?? [])
      .filter((id) => id !== agent.humanOwnerUserId)
      .map((id) => people.find((person) => person.id === id)?.name ?? id);
  }, [agent, people]);

  const canManage = may("create_edit_archive_agents");
  const sectionMeta = AGENT_NAV_ITEMS.find((item) => item.id === section);

  if (settingParam && !isAgentSettingsSectionId(settingParam)) {
    return <Navigate to={agentsHref(agentSectionPath(agentId, "dashboard"))} replace />;
  }

  if (!parsed || parsed.agentId !== agentId) {
    return <Navigate to={agentsHref(agentSectionPath(agentId, "dashboard"))} replace />;
  }

  if (section === "email" && !emailPane) {
    return (
      <Navigate to={agentsHref(agentSectionPath(agentId, "email", "personal"))} replace />
    );
  }

  if (!agent) {
    return (
      <div className="space-y-4">
        <Link
          to={agentsHref("/agents")}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:underline"
        >
          <ArrowLeft className="size-4" /> Back to agents
        </Link>
        <p className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
          Agent not found.
        </p>
      </div>
    );
  }

  const goSection = (next: AgentSectionId) => {
    navigate(agentsHref(agentSectionPath(agent.id, next)));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to={agentsHref("/agents")}
            className="mb-2 inline-flex items-center gap-1 text-sm text-gray-600 hover:underline dark:text-gray-400"
          >
            <ArrowLeft className="size-4" /> Agents
          </Link>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{agent.name}</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {agent.jobSummary?.trim()
              ? agent.jobSummary.trim()
              : `Owner ${agent.humanOwnerName} (${agent.humanOwnerEmail})`}
          </p>
          {agent.jobSummary?.trim() ? (
            <p className="mt-1 text-xs text-gray-500">
              Owner {agent.humanOwnerName} ({agent.humanOwnerEmail})
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={agent.status === "archived" ? "outline" : "default"}>
            {agent.status === "archived"
              ? "Archived"
              : agent.status === "paused"
                ? "Inactive"
                : "Active"}
          </Badge>
          <AgentActionsMenu
            status={agent.status}
            disabled={!canManage && agent.status !== "archived"}
            settingsItems={agentSettingsNavItems().map((item) => ({
              id: item.id,
              label: item.name,
              icon: item.icon,
              onSelect: () => goSection(item.id),
            }))}
            onEdit={() => goSection("dashboard")}
            onDuplicate={() => {
              if (!canManage) return;
              const id = duplicateAgent(agent.id);
              if (!id) {
                toast.error("Could not duplicate that agent.");
                return;
              }
              toast.success(`${agent.name} duplicated.`);
              navigate(agentsHref(agentSectionPath(id, "dashboard")));
            }}
            onActivate={() => {
              if (!canManage) return;
              setAgentStatus(agent.id, "active");
              toast.success(`${agent.name} is active.`);
            }}
            onDeactivate={() => {
              if (!canManage) return;
              setAgentStatus(agent.id, "paused");
              toast.success(`${agent.name} is inactive.`);
            }}
            onArchive={() => {
              if (!canManage) return;
              setArchiveError(null);
              setArchiveOpen(true);
            }}
          />
        </div>
      </div>

      {section === "dashboard" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Agent</CardTitle>
              <CardDescription>Named worker in this organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>
                <span className="text-muted-foreground">Name:</span> {agent.name}
              </p>
              <p>
                <span className="text-muted-foreground">Job:</span>{" "}
                {agent.jobSummary?.trim() || "Not set yet"}
              </p>
              <p>
                <span className="text-muted-foreground">Owner:</span> {agent.humanOwnerName} (
                {agent.humanOwnerEmail})
              </p>
              <p>
                <span className="text-muted-foreground">Shared with:</span>{" "}
                {sharedNames.length > 0 ? sharedNames.join(", ") : "Only the owner"}
              </p>
              <p>
                <span className="text-muted-foreground">Status:</span>{" "}
                {agent.status === "archived"
                  ? "Archived"
                  : agent.status === "paused"
                    ? "Inactive"
                    : "Active"}
              </p>
              <p>
                <span className="text-muted-foreground">Credits this month:</span>{" "}
                {agent.creditsBurnedMonth.toLocaleString()}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Organization</CardTitle>
              <CardDescription>{org.name}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>
                <span className="text-muted-foreground">Plan:</span> {org.planLabel}. Unlimited
                agents. Usage is credits.
              </p>
              <p>
                <span className="text-muted-foreground">Last active:</span>{" "}
                {new Date(agent.lastActiveAt).toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground">
                Agents are archived, never deleted, so timelines stay available.
              </p>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {section === "timeline" ? (
        <section className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Newest first. Tool calls, stops, and human resolutions stay here.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                toast.success("Timeline export is a stub. CSV/JSON export will land later.")
              }
            >
              <Download className="size-4" aria-hidden />
              Export
            </Button>
          </div>
          <ul className="space-y-3">
            {events.map((ev) => (
              <li
                key={ev.id}
                id={ev.id}
                className="rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-[#1D232D]"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">
                      {kindLabel(ev.kind)} · {ev.actor}
                      {ev.credits != null ? ` · ${ev.credits} credits` : ""}
                    </p>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">{ev.summary}</p>
                    {ev.externalRefs?.length ? (
                      <p className="mt-1 text-xs text-gray-500">
                        {ev.externalRefs.map((r) => `${r.label} ${r.id}`).join(" · ")}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {resultBadge(ev.result)}
                    <time className="text-xs text-gray-500" dateTime={ev.at}>
                      {new Date(ev.at).toLocaleString()}
                    </time>
                  </div>
                </div>
              </li>
            ))}
            {events.length === 0 ? (
              <li className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-gray-500">
                No timeline events yet for this agent.
              </li>
            ) : null}
          </ul>
        </section>
      ) : null}

      {section === "email" && emailPane ? (
        <section className="space-y-4">
          {viewOnly ? (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
              Standard role is view only. A Super Admin can edit Agent Configuration.
            </p>
          ) : null}
          <AgentEmailSection key={`${agent.id}-${emailPane}`} agent={agent} pane={emailPane} />
        </section>
      ) : null}

      {section === "schedules" ? (
        <section className="space-y-4">
          {viewOnly ? (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
              Standard role is view only. A Super Admin can edit Agent Configuration.
            </p>
          ) : null}
          <AgentScheduleSection key={`${agent.id}-schedules`} agent={agent} />
        </section>
      ) : null}

      {sectionMeta?.kind === "config" &&
      section !== "email" &&
      section !== "schedules" ? (
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle className="text-base">{sectionMeta.name}</CardTitle>
            <CardDescription>{sectionMeta.body}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {viewOnly ? (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
                Standard role is view only. A Super Admin can edit Agent Configuration.
              </p>
            ) : null}
            {section === "knowledge" ? (
              <Link
                to={agentsHref("/knowledge")}
                className="inline-flex h-8 items-center rounded-md border border-gray-300 px-3 text-xs font-semibold hover:bg-gray-50 dark:border-white/20"
              >
                Open Knowledge
              </Link>
            ) : section === "connections" ? (
              <Link
                to={agentsHref("/connections")}
                className="inline-flex h-8 items-center rounded-md border border-gray-300 px-3 text-xs font-semibold hover:bg-gray-50 dark:border-white/20"
              >
                Manage org connections
              </Link>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!may("manage_agent_configuration")}
                onClick={() =>
                  toast.success(`${sectionMeta.name} editor is a placeholder for Phase 1.`)
                }
              >
                {may("manage_agent_configuration") ? "Edit" : "View only"}
              </Button>
            )}
            {section === "owner" ? (
              <p className="text-sm text-muted-foreground">
                Current owner: {agent.humanOwnerName} ({agent.humanOwnerEmail})
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <ArchiveAgentDialog
        open={archiveOpen}
        agentName={agent.name}
        busy={archiveBusy}
        error={archiveError}
        onCancel={() => {
          if (archiveBusy) return;
          setArchiveOpen(false);
          setArchiveError(null);
        }}
        onConfirm={(password) => {
          if (!password.trim()) {
            setArchiveError("Enter your password to agree to archive this agent.");
            return;
          }
          setArchiveBusy(true);
          setArchiveError(null);
          archiveAgent(agent.id);
          toast.success(`${agent.name} archived. History is kept.`);
          setArchiveBusy(false);
          setArchiveOpen(false);
        }}
      />
    </div>
  );
}
